import { useState, useEffect, useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { LIMIT_ERROR_ID, type UploaderError } from './use-image-uploader';
import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import useMediaUploader from '../../../../common/use-media-uploader';
import type {
  ContentFile,
  MediaGroup,
} from '../../../../models/user-generated-contents/types';
import { ACCEPTED_MIME_TYPES } from './file-types';

export const MEDIA_GROUP_TYPE = 'files' as const;
export const MAX_FILES = 10;
export const MAX_FILE_SIZE_MB = 100;
export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
export const ACCEPTED_FILE_TYPES = ACCEPTED_MIME_TYPES.join(', ');

export type UseFileUploaderReturn = {
  files: ContentFile[];
  inProgress: File[];
  totalCount: number;
  isAtLimit: boolean;
  isUploading: boolean;
  errors: UploaderError[];
  // True while an over-limit error is being shown; drives the counter's error state.
  limitError: boolean;
  clearErrors: () => void;
  // Dismiss a single error by its position in `errors` (the alert's close button).
  dismissError: (index: number) => void;
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
  open: ReturnType<typeof useDropzone>['open'];
  handleDropAccepted: (files: File[]) => void;
  handleDropRejected: (rejections: FileRejection[]) => void;
  handleDelete: (index: number) => void;
  handleReorder: (files: ContentFile[]) => void;
};

const useFileUploader = (): UseFileUploaderReturn => {
  const { t } = useTranslation();
  const { userGeneratedContent, updateAttachmentGroup } =
    useUserGeneratedContent();

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<UploaderError[]>([]);
  const limitError = errors.some((error) => error.id === LIMIT_ERROR_ID);

  const attachments = userGeneratedContent.attachments ?? [];
  const filesGroup = attachments.find(
    (g): g is Extract<MediaGroup, { type: typeof MEDIA_GROUP_TYPE }> =>
      g.type === MEDIA_GROUP_TYPE
  );
  const files: ContentFile[] = filesGroup?.items ?? [];

  const totalCount = files.length + (currentFile ? 1 : 0) + pendingFiles.length;
  const isAtLimit = totalCount >= MAX_FILES;

  const { status } = useMediaUploader({
    file: currentFile,
    onStatusChange: undefined,
    onComplete: (url: string) => {
      const fileName = currentFile?.name ?? '';
      const fileType = currentFile?.type ?? '';
      const fileSize = currentFile?.size;
      updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => [
        ...prev,
        {
          url,
          file_name: fileName,
          file_type: fileType,
          file_size: fileSize,
          order: prev.length,
        },
      ]);
      setCurrentFile(null);
    },
    onError: () => {
      setErrors([
        {
          id: 'upload',
          title: '',
          description: t('user_generated_content.media.files.error_upload'),
        },
      ]);
      setCurrentFile(null);
    },
  });

  const isUploading = status === 'fetching' || status === 'uploading';

  useEffect(() => {
    if (!currentFile && pendingFiles.length > 0) {
      const [next, ...rest] = pendingFiles;
      setCurrentFile(next);
      setPendingFiles(rest);
    }
  }, [currentFile, pendingFiles]);

  const handleDropAccepted = useCallback(
    (droppedFiles: File[]) => {
      const available = MAX_FILES - totalCount;
      if (droppedFiles.length > available) {
        setErrors([
          {
            id: LIMIT_ERROR_ID,
            title: '',
            description: t(
              'user_generated_content.media.files.error_too_many_files',
              { max: MAX_FILES }
            ),
          },
        ]);
      } else {
        setErrors([]);
      }
      setPendingFiles((prev) => [...prev, ...droppedFiles.slice(0, available)]);
    },
    [t, totalCount]
  );

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const errs = new Map<string, UploaderError>();
      for (const rejection of rejections) {
        const code = rejection?.errors[0]?.code;
        if (code === 'too-many-files') {
          errs.set('too-many-files', {
            id: LIMIT_ERROR_ID,
            title: '',
            description: t(
              'user_generated_content.media.files.error_too_many_files',
              { max: MAX_FILES }
            ),
          });
        } else if (code === 'file-too-large') {
          errs.set('file-too-large', {
            id: 'file-too-large',
            title: t('user_generated_content.media.error_too_large_title'),
            description: t('user_generated_content.media.error_too_large', {
              maxSize: MAX_FILE_SIZE_MB,
            }),
          });
        } else {
          errs.set('rejected', {
            id: 'rejected',
            title: t('common.error_rejected_title'),
            description: t(
              'user_generated_content.media.files.error_rejected',
              { maxSize: MAX_FILE_SIZE_MB }
            ),
          });
        }
      }
      setErrors(Array.from(errs.values()));
    },
    [t]
  );

  const { getInputProps, open } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    maxFiles: MAX_FILES,
    multiple: true,
    noClick: true,
    noDrag: true,
    disabled: isAtLimit,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected,
  });

  const handleDelete = useCallback(
    (index: number) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => {
        const next = prev
          .filter((_, i) => i !== index)
          .map((f, i) => ({ ...f, order: i }));
        return next.length > 0 ? next : undefined;
      });
      setErrors([]);
    },
    [updateAttachmentGroup]
  );

  const handleReorder = useCallback(
    (reordered: ContentFile[]) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, () => reordered);
      setErrors([]);
    },
    [updateAttachmentGroup]
  );

  const clearErrors = useCallback(() => setErrors([]), []);

  const dismissError = useCallback((index: number) => {
    setErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const inProgress = currentFile
    ? [currentFile, ...pendingFiles]
    : pendingFiles;

  return {
    files,
    inProgress,
    totalCount,
    isAtLimit,
    isUploading,
    errors,
    limitError,
    clearErrors,
    dismissError,
    getInputProps,
    open,
    handleDropAccepted,
    handleDropRejected,
    handleDelete,
    handleReorder,
  };
};

export default useFileUploader;
